import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider
} from '@mui/material'
import {
  CreditCard,
  Warning,
  TrendingUp,
  People,
  AttachMoney
} from '@mui/icons-material'
import { BillingService } from '../../../../shared/services/BillingService'

interface BillingInfo {
  billingPlan: string
  monthlyAmount: number
  maxAgents: number
  currentAgents: number
  trialEndsAt?: string
  accountStatus: string
  daysRemaining: number
  isTrialActive: boolean
  hasStripeCustomer: boolean
  hasActiveSubscription: boolean
  purchaseSource?: string
}

function AdminBilling() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null)

  useEffect(() => {
    loadBillingInfo()
  }, [])

  const loadBillingInfo = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch billing information from Tenant (not Organisation)
      const data = await BillingService.getTrialStatus()

      setBillingInfo({
        billingPlan: data.billingPlan,
        monthlyAmount: data.monthlyAmount,
        maxAgents: 5, // Default value - will be updated when user purchases
        currentAgents: 0, // TODO: Get actual agent count from users table
        trialEndsAt: data.trialEndsAt,
        accountStatus: data.accountStatus,
        daysRemaining: data.daysRemaining,
        isTrialActive: data.isTrialActive,
        hasStripeCustomer: data.hasStripeCustomer,
        hasActiveSubscription: data.hasActiveSubscription,
        purchaseSource: data.purchaseSource
      })
    } catch (err: any) {
      console.error('Failed to load billing information:', err)
      setError(err.message || 'Failed to load billing information')
    } finally {
      setLoading(false)
    }
  }

  const getPlanColor = (plan: string): 'default' | 'primary' | 'secondary' | 'success' => {
    switch (plan.toLowerCase()) {
      case 'enterprise':
        return 'success'
      case 'professional':
        return 'primary'
      case 'basic':
        return 'secondary'
      default:
        return 'default'
    }
  }

  const isTrialActive = billingInfo?.isTrialActive || false
  const agentUtilization = billingInfo ? (billingInfo.currentAgents / billingInfo.maxAgents) * 100 : 0

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !billingInfo) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Billing information not available'}
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Billing & Subscription
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your subscription and billing information
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (billingInfo?.purchaseSource === 'Marketplace') {
              window.location.href = 'https://portal.azure.com/#view/HubsExtension/BrowseResourceBlade/resourceType/Microsoft.SaaS%2Fresources'
            } else {
              navigate('/billing/upgrade')
            }
          }}
        >
          {billingInfo?.purchaseSource === 'Marketplace' ? 'Manage in Azure Portal' : 'Upgrade Plan'}
        </Button>
      </Box>

      {/* Trial Warning (if applicable) */}
      {isTrialActive && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Trial Period Active
          </Typography>
          <Typography variant="body2">
            Your trial ends on {new Date(billingInfo.trialEndsAt!).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            })}. Upgrade to continue using the service after the trial period.
          </Typography>
        </Alert>
      )}

      {/* Current Plan Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CreditCard color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Current Plan
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {billingInfo.billingPlan}
                </Typography>
                <Chip
                  label={billingInfo.isTrialActive ? 'Trial' : billingInfo.accountStatus === 'active' ? 'Active' : 'Inactive'}
                  color={billingInfo.isTrialActive ? 'primary' : billingInfo.accountStatus === 'active' ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {billingInfo.isTrialActive && billingInfo.trialEndsAt ?
                  `Trial ends: ${new Date(billingInfo.trialEndsAt).toLocaleDateString()}` :
                  `Status: ${billingInfo.accountStatus}`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AttachMoney color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Monthly Cost
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                £{billingInfo.monthlyAmount.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Billed monthly
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <People color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Agent Licenses
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {billingInfo.currentAgents}
                </Typography>
                <Typography variant="h5" color="text.secondary">
                  / {billingInfo.maxAgents}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {agentUtilization.toFixed(0)}% utilized
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Billing Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
            Subscription Details
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Plan Name
                </Typography>
                <Chip
                  label={billingInfo.billingPlan}
                  color={getPlanColor(billingInfo.billingPlan)}
                  sx={{ mt: 0.5, fontWeight: 600 }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Account Status
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {billingInfo.accountStatus}
                </Typography>
              </Box>

              {billingInfo.trialEndsAt && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Trial End Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {new Date(billingInfo.trialEndsAt).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </Typography>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Monthly Amount
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                  £{billingInfo.monthlyAmount.toFixed(2)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Maximum Agents
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {billingInfo.maxAgents} licenses
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Current Agents
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {billingInfo.currentAgents} active
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Agent Utilization Warning */}
          {agentUtilization >= 90 && (
            <Alert severity="warning" icon={<TrendingUp />}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Agent License Utilization High
              </Typography>
              <Typography variant="body2">
                You're using {agentUtilization.toFixed(0)}% of your agent licenses.
                Consider upgrading your plan to add more agents.
              </Typography>
            </Alert>
          )}

          {agentUtilization >= 100 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Agent License Limit Reached
              </Typography>
              <Typography variant="body2">
                You've reached your maximum number of agent licenses.
                Upgrade your plan to add more agents.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

    </Box>
  )
}

export default AdminBilling
